import {useEffect, useState} from 'react';
import mapboxgl from 'mapbox-gl';
import * as LucidActions from 'common/actions/actions';
import {FloorPlanTypeEnum} from 'common/proxies/enum-proxy';
import {t} from 'common/utils/translation-util';

export const DEFAULT_ZOOM = 20;

export const useSavePosition = (map, reportId, loaded) => {
  const [moved, setMoved] = useState(false);
  if (!map || !loaded) {
    return [moved, () => null];
  }
  const onSavePosition = () => {
    const newCenter = map.getCenter();
    const newBearing = map.getBearing();
    const newPitch = map.getPitch();
    const newZoom = parseInt(map.getZoom(), 10);
    const currentPosition = {
      bearing: newBearing,
      latitude: newCenter.lat,
      longitude: newCenter.lng,
      pitch: newPitch,
      zoom: newZoom,
    };
    LucidActions.updateReportConfiguration(reportId, currentPosition);
    setMoved(false);
  };
  const onMapMove = () => {
    if (!moved) {
      setMoved(true);
    }
  };

  map.off('moveend', onMapMove);
  map.on('moveend', onMapMove);

  return [moved, onSavePosition];
};

export const useMap = (mapRef, options, dependencies = []) => {
  const [error, setError] = useState();
  const [loaded, setLoaded] = useState(false);
  const [map, setMap] = useState();
  useEffect(() => {
    const {forExport, ...mapOptions} = options;
    const mapInstance = new mapboxgl.Map({
      attributionControl: false,
      buffer: 0,
      container: mapRef.current,
      preserveDrawingBuffer: true, // needed for exports
      scrollZoom: false,
      zoom: DEFAULT_ZOOM,
      transformRequest: (url) => {
        if (url.indexOf(window.settings.hosts.api) > -1) {
          return {
            url,
            credentials: 'include',
          };
        }
      },
      ...mapOptions,
    });

    const onLoad = () => {
      if (!forExport) {
        mapInstance.addControl(
          new mapboxgl.NavigationControl({showCompass: false}),
          'top-left',
        );
      }
      setLoaded(true);
    };
    const onError = () => {
      setError('Error when loading data source');
    };
    mapInstance.on('error', onError);
    mapInstance.on('load', onLoad);
    setMap(mapInstance);
    return () => {
      if (map) {
        map.stop();
        map.off('error', onError);
        map.off('load', onLoad);
        map.off('moveend');
        map.remove();
      }
      setMap(null);
      setLoaded(false);
    };
  }, dependencies);
  return [map, error, loaded];
};

export const useLoading = (id, map, layers = [], sources = []) => {
  const [loadingSources, setLoadingSources] = useState(sources);
  const [layersLoaded, setLayersLoaded] = useState(false);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const onLoadData = (evt) => {
    if (!sourcesLoaded && evt.dataType === 'source') {
      const sourceIndex = loadingSources.indexOf(evt.sourceId);
      if (sourceIndex > -1) {
        const remainingSources = Array.from(loadingSources);
        if (
          Boolean(map.getSource(evt.sourceId)) &&
          map.isSourceLoaded(evt.sourceId)
        ) {
          remainingSources.splice(sourceIndex, 1);
          if (remainingSources.length === 0) {
            setSourcesLoaded(true);
            map.off('data', onLoadData);
          }
          setLoadingSources(remainingSources);
        }
      }
    }
  };

  //  This is fix for a mapbox issue. Mapbox isn't firing correctly `style.load` event.
  //  https://github.com/mapbox/mapbox-gl-js/issues/2268#issuecomment-401979967
  const onStyleLoad = () => {
    const timeout = 2000;
    const waitingStyle = (styleLayers) => {
      const remainingLayers = Array.from(styleLayers).filter(
        (layer) => !map.getLayer(layer),
      );

      if (remainingLayers.length === 0) {
        setLayersLoaded(true);
        map.off('style.load', onStyleLoad);
      } else {
        setTimeout(() => waitingStyle(remainingLayers), timeout);
      }
    };
    waitingStyle(layers);
  };
  if (map) {
    if (!layersLoaded) {
      map.on('style.load', onStyleLoad);
    }
    if (!sourcesLoaded) {
      map.on('data', onLoadData);
    }
  }
  return !layersLoaded || !sourcesLoaded;
};

export const useFloorImageLayer = (
  map,
  floor,
  mapLoaded,
  dependencies = [],
) => {
  const [error, setError] = useState();
  const loading = useLoading('floor', map, ['floor'], ['floor']);
  useEffect(() => {
    if (map && floor && mapLoaded) {
      const source = map.addSource('floor', {
        type: 'raster',
        tiles: [floor.floorPlan.tileUrl],
      });
      source.on('error', (evt) => {
        const statusCode = evt.error.status;
        if (statusCode === 401) {
          if (floor.floorPlan.type.id === FloorPlanTypeEnum.ATRIUS_TILE.id) {
            setError(
              t(`
              Unable to render floorplan. Please make
              sure the Atrius integration is properly
              configured.
            `),
            );
          }
        } else if (statusCode === 501) {
          if (floor.floorPlan.type.id === FloorPlanTypeEnum.ATRIUS_TILE.id) {
            setError(
              t(
                `
              Unable to render floorplan. This floorplan
              is configured using Atrius Tile Service but is
              hosted by a third-party service. Please
              reconfigure this floor with the third-party
              service at %(profileUrl)s
            `,
                {
                  profileUrl: `buildingos.com${window.settings.urls.buildingSave.replace(
                    0,
                    floor.buildingId,
                  )}#area`,
                },
              ),
            );
          }
        } else if (statusCode !== 200) {
          setError(
            t(`
            Unable to render floorplan due to an
            unexpected error. Please contact
            Customer Support.
          `),
          );
        }
      });

      map.addLayer({
        id: 'floor',
        source: 'floor',
        type: 'raster',
      });
    }
    return () => {
      if (map) {
        if (map.getLayer('floor')) {
          map.removeLayer('floor');
        }
        const floorSource = map.getSource('floor');
        if (floorSource) {
          floorSource.off('error');
          map.removeSource('floor');
        }
      }
    };
  }, dependencies);
  return [error, floor && loading];
};
